import { useEffect, useCallback } from 'react';
import hubspotService from '../services/hubspotService';
import { HubspotUser } from '../types/hubspot';

interface UseHubspotProps {
  user: HubspotUser | null;
  enabled?: boolean;
}

interface UseHubspotReturn {
  showHubspot: () => void;
  hideHubspot: () => void;
  isReady: boolean;
}

/**
 * Custom hook to manage HubSpot lifecycle
 *
 * @param user - User object with HubSpot data
 * @param enabled - Whether HubSpot should be initialized (default: true)
 * @returns Object with methods to control the HubSpot widget
 */
export const useHubspot = ({
  user,
  enabled = true,
}: UseHubspotProps): UseHubspotReturn => {
  useEffect(() => {
    if (!enabled || !user) {
      return;
    }

    hubspotService.boot(user);

    // Don't shutdown on unmount, only on explicit logout
    // This prevents re-initialization on route changes
  }, [user, enabled]);

  const showHubspot = useCallback(() => {
    hubspotService.show();
  }, []);

  const hideHubspot = useCallback(() => {
    hubspotService.hide();
  }, []);

  return {
    showHubspot,
    hideHubspot,
    isReady: hubspotService.isReady(),
  };
};

export default useHubspot;
